import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Linkedin, Loader2, MailCheck, Phone, Mail } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Regulatory Advisory",
  "Risk Assessment & Gap Analysis",
  "Compliance Program Implementation",
  "Policy & Procedure Development",
  "Regulatory Reporting",
  "Regulatory Onsite Reviews",
  "Compliance Training",
  "Mentoring & Coaching",
];

const INITIAL = { full_name: "", email: "", company: "", service_interest: "", message: "" };

const inputCls =
  "w-full bg-white border border-[#e6e1d5] rounded-xl px-4 py-3.5 text-sm text-[#1b2a4e] placeholder:text-[#8a93a8] focus:outline-none focus:ring-2 focus:ring-[#a8824f] focus:border-transparent transition-all";

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received", {
        description: "Thank you — ComplyWise Consult will respond within one business day.",
      });
      setForm(INITIAL);
    } catch (err) {
      toast.error("Could not send your enquiry", {
        description: "Please try again, or reach out via LinkedIn.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-28 px-6 bg-[#1b2a4e]">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-5">
          <Eyebrow>Advisory Enquiries</Eyebrow>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.1]">
            Begin the <span className="italic text-[#d3b98c]">conversation.</span>
          </h2>
          <p className="text-white/70 mt-6 text-sm sm:text-base leading-relaxed max-w-md">
            Whether you are preparing for a regulatory onsite review, strengthening your compliance
            programme or building your team's capability — share your scope and ComplyWise Consult
            will respond within one business day.
          </p>
          <div className="mt-10 space-y-4">
            <div className="flex items-center gap-3 text-white/80 text-sm">
              <span className="p-2 rounded-lg bg-white/[0.06] border border-white/10 text-[#d3b98c]">
                <MailCheck size={16} />
              </span>
              Every enquiry is read personally by the Managing Director.
            </div>
            <a
              data-testid="contact-email-link"
              href="mailto:consult@complywise.co.uk"
              className="flex items-center gap-3 text-white/80 hover:text-white text-sm transition-colors w-fit focus:outline-none focus:ring-2 focus:ring-[#a8824f] rounded"
            >
              <span className="p-2 rounded-lg bg-white/[0.06] border border-white/10 text-[#d3b98c]">
                <Mail size={16} />
              </span>
              consult@complywise.co.uk
            </a>
            <a
              data-testid="contact-phone-link"
              href="tel:+442070000000"
              className="flex items-center gap-3 text-white/80 hover:text-white text-sm transition-colors w-fit focus:outline-none focus:ring-2 focus:ring-[#a8824f] rounded"
            >
              <span className="p-2 rounded-lg bg-white/[0.06] border border-white/10 text-[#d3b98c]">
                <Phone size={16} />
              </span>
              +44 (0)20 7000 0000
            </a>
            <a
              data-testid="contact-linkedin-link"
              href="https://www.linkedin.com/in/janice-acquaah-5a3b0a8/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-white/80 hover:text-white text-sm transition-colors w-fit focus:outline-none focus:ring-2 focus:ring-[#a8824f] rounded"
            >
              <span className="p-2 rounded-lg bg-white/[0.06] border border-white/10 text-[#d3b98c]">
                <Linkedin size={16} />
              </span>
              Connect on LinkedIn
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="rounded-3xl bg-white border border-white/10 p-8 sm:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.25)]"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name-input" className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-2">Full Name</label>
                <input id="contact-name-input" data-testid="contact-name-input" required value={form.full_name} onChange={set("full_name")} placeholder="Your full name" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-email-input" className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-2">Corporate Email</label>
                <input id="contact-email-input" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="name@firm.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-company-input" className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-2">Institution / Firm</label>
                <input id="contact-company-input" data-testid="contact-company-input" required value={form.company} onChange={set("company")} placeholder="Your firm" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-service-select" className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-2">Primary Service Needed</label>
                <select id="contact-service-select" data-testid="contact-service-select" required value={form.service_interest} onChange={set("service_interest")} className={`${inputCls} appearance-none`}>
                  <option value="" disabled>Select a service</option>
                  {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-message-input" className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#a8824f] block mb-2">Enquiry / Scope Summary</label>
                <textarea id="contact-message-input" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Briefly describe your regulatory or compliance requirement..." className={`${inputCls} resize-none`} />
              </div>
            </div>
            <button
              data-testid="contact-form-submit-button"
              type="submit"
              disabled={sending}
              className="group mt-8 w-full sm:w-auto flex items-center justify-center gap-2.5 bg-[#1b2a4e] hover:bg-[#a8824f] disabled:opacity-50 text-white text-sm font-semibold px-9 py-4 rounded-full hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#a8824f]"
            >
              {sending ? <Loader2 size={17} className="animate-spin" /> : <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />}
              {sending ? "Sending..." : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
